import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Loader2, LogOut, Package, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";

export default function Account() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState({});
  const { add, setOpen } = useCart();
  const { items: wishlistItems, toggle: toggleWishlist, refresh: refreshWishlist } = useWishlist();

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false)).finally(() => setChecking(false));
    api.get("/products").then(({ data }) => setProducts(Object.fromEntries(data.map((p) => [p.id, p])))).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/account/orders").then(({ data }) => setOrders(data)).catch(() => {});
      refreshWishlist();
    }
  }, [user, refreshWishlist]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "register" ? { name, email, password } : { email, password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      setUser(data);
      toast.success(mode === "register" ? "Welcome to the Locka Circle" : `Welcome back, ${data.name}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    setOrders([]);
  };

  const reorder = (order) => {
    let added = 0;
    order.items.forEach((i) => {
      const p = products[i.product_id];
      if (p) {
        add(p, i.size || "M", i.quantity);
        added += 1;
      }
    });
    if (added) setOpen(true);
    else toast.error("Those pieces have left the collection", { description: "They were limited — the vault has closed." });
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="account-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" strokeWidth={1.4} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pt-24 pb-16" data-testid="account-auth-page">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="mb-10 text-center">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.34em] text-burgundy">The Locka Circle</p>
            <h1 className="mt-3 font-serif text-4xl text-navy" data-testid="account-auth-title">
              {mode === "login" ? "Step back in" : "Claim your pair of keys"}
            </h1>
            <p className="mt-3 text-sm text-navy/55">Orders, reordering and first access to drops.</p>
          </div>
          <div className="mb-8 grid grid-cols-2 border border-navy/15" data-testid="account-mode-tabs">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); }}
                data-testid={`account-mode-${m}`}
                className={`py-3.5 text-[0.65rem] font-bold uppercase tracking-[0.26em] transition-colors duration-300 ${mode === m ? "bg-navy text-cream" : "text-navy/50 hover:text-navy"}`}
              >
                {m === "login" ? "Sign In" : "Join"}
              </button>
            ))}
          </div>
          <form onSubmit={submit} data-testid="account-auth-form">
            {mode === "register" && (
              <label className="mb-5 block">
                <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Name</span>
                <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input"
                  className="h-12 w-full border border-navy/20 bg-white/5 px-4 text-navy focus:border-gold focus:outline-none" placeholder="Ava Beaumont" />
              </label>
            )}
            <label className="mb-5 block">
              <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email-input"
                className="h-12 w-full border border-navy/20 bg-white/5 px-4 text-navy focus:border-gold focus:outline-none" placeholder="you@finest-address.com" />
            </label>
            <label className="mb-7 block">
              <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Password</span>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="account-password-input"
                className="h-12 w-full border border-navy/20 bg-white/5 px-4 text-navy focus:border-gold focus:outline-none" placeholder="Six characters minimum" />
            </label>
            {error && <p className="mb-5 text-sm text-burgundy" data-testid="account-auth-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="account-auth-submit"
              className="group relative w-full overflow-hidden bg-navy py-4 text-[0.7rem] font-bold uppercase tracking-[0.28em] text-cream disabled:opacity-60">
              <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative transition-colors duration-500 group-hover:text-[#0B101E]">
                {busy ? "One moment…" : mode === "login" ? "Sign In" : "Join the Circle"}
              </span>
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen max-w-4xl px-6 pt-36 pb-24" data-testid="account-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.34em] text-burgundy">The Locka Circle</p>
          <h1 className="mt-3 font-serif text-4xl text-navy sm:text-5xl" data-testid="account-greeting">
            Bonjour, <em className="text-gold">{user.name}</em>
          </h1>
          <p className="mt-2 text-sm text-navy/50">{user.email}</p>
        </div>
        <button onClick={logout} data-testid="account-logout-button"
          className="flex items-center gap-2 border border-navy/20 px-6 py-3 text-[0.65rem] font-bold uppercase tracking-[0.24em] text-navy transition-colors duration-300 hover:border-burgundy hover:text-burgundy">
          <LogOut className="h-4 w-4" strokeWidth={1.8} /> Sign Out
        </button>
      </div>

      <div className="mt-14" data-testid="account-orders">
        <p className="mb-6 flex items-center gap-3 text-[0.65rem] font-bold uppercase tracking-[0.3em] text-navy/50">
          <Package className="h-4 w-4" strokeWidth={1.6} /> Your Orders
        </p>
        {orders.length === 0 ? (
          <div className="border border-navy/10 bg-white/5 p-12 text-center" data-testid="account-orders-empty">
            <p className="font-serif text-2xl italic text-navy/60">No steps taken yet.</p>
            <p className="mt-3 text-sm text-navy/45">Your confirmed orders will appear here, ready to reorder in one tap.</p>
          </div>
        ) : (
          <ul className="space-y-5">
            {orders.map((o) => (
              <motion.li
                key={o.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-navy/10 bg-white/5 p-6"
                data-testid={`account-order-${o.id.slice(0, 8)}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-xs text-navy/40">#{o.id.slice(0, 8)} · {new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                    <ul className="mt-3 space-y-1.5">
                      {o.items.map((i, idx) => (
                        <li key={idx} className="text-sm text-navy/80">
                          <span className="font-serif text-base">{i.name}</span>
                          {i.size ? <span className="text-navy/50"> · Size {i.size}</span> : null}
                          <span className="text-navy/50"> × {i.quantity}</span>
                        </li>
                      ))}
                    </ul>
                    {(o.fulfillment === "shipped" || o.fulfillment === "delivered") && (
                      <p className="mt-3.5 text-[0.62rem] font-bold uppercase tracking-[0.22em]" data-testid={`order-fulfillment-${o.id.slice(0, 8)}`}>
                        {o.fulfillment === "delivered" ? (
                          <span className="text-gold">Delivered — step proudly</span>
                        ) : o.tracking_url ? (
                          <span className="text-navy/60">
                            Shipped ·{" "}
                            <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`tracking-link-${o.id.slice(0, 8)}`} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-soft">
                              Track parcel {o.tracking_number}
                            </a>
                          </span>
                        ) : (
                          <span className="text-navy/60">Shipped{o.tracking_number ? ` · Tracking ${o.tracking_number}` : ""}</span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-2xl text-gold">${o.amount.toFixed(2)}</p>
                    <span className={`mt-1 inline-block px-2.5 py-1 text-[0.58rem] font-bold uppercase tracking-[0.2em] ${o.payment_status === "paid" ? "bg-navy text-gold" : "bg-navy/10 text-navy/50"}`}>
                      {o.payment_status}
                    </span>
                  </div>
                </div>
                {o.payment_status === "paid" && (
                  <button
                    onClick={() => reorder(o)}
                    data-testid={`account-reorder-${o.id.slice(0, 8)}`}
                    className="group relative mt-5 overflow-hidden border border-navy px-7 py-3 text-[0.62rem] font-bold uppercase tracking-[0.26em] text-navy transition-colors duration-300"
                  >
                    <span className="absolute inset-0 -translate-x-full bg-navy transition-transform duration-500 ease-out group-hover:translate-x-0" />
                    <span className="relative flex items-center gap-2 transition-colors duration-500 group-hover:text-gold">
                      <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.8} /> Reorder These Pieces
                    </span>
                  </button>
                )}
              </motion.li>
            ))}
          </ul>
        )}
      </div>

      {wishlistItems.length > 0 && (
        <div className="mt-14" data-testid="account-wishlist">
          <p className="mb-6 flex items-center gap-3 text-[0.65rem] font-bold uppercase tracking-[0.3em] text-navy/50">
            <Heart className="h-4 w-4 fill-burgundy text-burgundy" strokeWidth={1.6} /> Saved for Later
          </p>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
            {wishlistItems.map((p) => (
              <div key={p.id} className="group border border-navy/10 bg-white/5" data-testid={`wishlist-item-${p.slug}`}>
                <div className="relative aspect-square overflow-hidden">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <button onClick={() => toggleWishlist(p)} aria-label={`Remove ${p.name} from wishlist`} data-testid={`wishlist-remove-${p.slug}`} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#0B101E]/70 backdrop-blur transition-colors duration-200 hover:bg-burgundy">
                    <Heart className="h-4 w-4 fill-burgundy text-burgundy" />
                  </button>
                </div>
                <div className="p-4">
                  <p className="font-serif text-base leading-tight text-navy">{p.name}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-serif text-sm text-gold">${p.price.toFixed(2)}</span>
                    <button
                      onClick={() => {
                        add(p, "M");
                        setOpen(true);
                      }}
                      data-testid={`wishlist-add-${p.slug}`}
                      className="text-[0.58rem] font-bold uppercase tracking-[0.2em] text-burgundy transition-colors duration-200 hover:text-gold"
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
